import type {IssueStateReasonType, IssueStateType} from '@github-ui/issue-pr-state/constants'
import {AlertFillIcon, XIcon} from '@primer/octicons-react'
import {IconButton, Tooltip} from '@primer/react'
import {useEffect} from 'react'

import {LABELS} from './constants/labels'
import type {PotentialDuplicateIssueUpdate} from './hooks/use-duplicate-detection'
import styles from './PotentialDuplicates.module.css'
import {PotentialDuplicatesMenu} from './PotentialDuplicatesMenu'
import {usePotentialDuplicateSubscription} from './subscriptions/potential-duplicate-subscription'

const noop = () => {}
export type IssueToPreheat = {owner: string; repo: string; number: number}
export type PotentialDuplicateIssue = {
  id: string
  /** GraphQL node ID, used for subscriptions. */
  nodeId: string
  number: number
  title: string
  state: IssueStateType
  stateReason: IssueStateReasonType
  score?: number | null
}

export type PotentialDuplicatesProps = {
  items: readonly PotentialDuplicateIssue[]
  isError: boolean
  repoName: string
  repoOwner: string
  /** When provided, clicking an issue will call this handler to open the side panel.
   * Cmd/Ctrl+click still opens the issue in a new tab. */
  onIssueClick?: (issue: PotentialDuplicateIssue) => void
  /** Called when an issue is updated via subscription */
  onIssueUpdate?: (update: PotentialDuplicateIssueUpdate) => void
  /** Called when an issue is clicked for analytics tracking. Receives position in list. */
  onIssueClickTracking?: (position: number) => void
  /** Called when the list of issues to preheat changes. Used by parent to preheat IssueViewer queries. */
  onIssuesToPreheat?: (issues: IssueToPreheat[]) => void
  /** Key to reset subscriptions when session changes. */
  sessionResetKey?: number
  onDismiss?: () => void
}

/** Inner component that subscribes to a single issue - only rendered when we have valid props */
function IssueSubscriptionInner({
  nodeId,
  onIssueUpdate,
}: {
  nodeId: string
  onIssueUpdate: (update: PotentialDuplicateIssueUpdate) => void
}) {
  usePotentialDuplicateSubscription(nodeId, {onUpdate: onIssueUpdate})
  return null
}

/** Wrapper that conditionally subscribes to all issues in the list */
function IssueSubscriptions({
  items,
  onIssueUpdate,
}: {
  items: readonly PotentialDuplicateIssue[]
  onIssueUpdate?: (update: PotentialDuplicateIssueUpdate) => void
}) {
  if (!onIssueUpdate) {
    return null
  }
  return (
    <>
      {items.map(item => (
        <IssueSubscriptionInner key={item.nodeId} nodeId={item.nodeId} onIssueUpdate={onIssueUpdate} />
      ))}
    </>
  )
}

/**
 * Displays potential duplicate issues as a split button with label.
 * Left button shows first issue with hovercard, right dropdown shows remaining issues.
 * Supports SidePanel viewing via onIssueClick and real-time updates via subscriptions.
 */
export function PotentialDuplicates({
  items,
  isError,
  repoOwner,
  repoName,
  onIssueClick,
  onIssueUpdate,
  onIssueClickTracking,
  onIssuesToPreheat,
  sessionResetKey,
  onDismiss,
}: PotentialDuplicatesProps) {
  // Notify parent of issues to preheat when side panel mode is active.
  useEffect(() => {
    if (!onIssuesToPreheat || items.length === 0) return
    const issuesToPreheat = items.map(item => ({owner: repoOwner, repo: repoName, number: item.number}))
    onIssuesToPreheat(issuesToPreheat)
  }, [items, repoOwner, repoName, onIssuesToPreheat])

  if (items.length === 0 || isError) {
    return null
  }

  return (
    <div data-issue-and-pr-hovercards-enabled className={styles.container} key={sessionResetKey}>
      <IssueSubscriptions items={items} onIssueUpdate={onIssueUpdate} />
      <div id="potential-duplicates-label" className={styles.labelGroup}>
        <AlertFillIcon size={12} className={styles.warningIcon} />
        <span className={styles.label}>
          {items.length === 1 ? LABELS.potentialDuplicate : LABELS.duplicatesCount(items.length)}
        </span>
      </div>
      <div className={styles.actionsGroup}>
        <PotentialDuplicatesMenu
          items={items}
          repoOwner={repoOwner}
          repoName={repoName}
          onIssueClick={onIssueClick}
          trackIssueClicked={onIssueClickTracking ?? noop}
        />
        {onDismiss && (
          <>
            <Tooltip type="description" direction="n" text={LABELS.dismissDuplicatesTooltip}>
              <IconButton
                aria-labelledby="dismiss-duplicates-tooltip"
                icon={XIcon}
                variant="invisible"
                size="small"
                onClick={onDismiss}
                tooltipDirection="n"
              />
            </Tooltip>
            <span className="sr-only" id="dismiss-duplicates-tooltip">
              {LABELS.dismissDuplicates(items.length)}
            </span>
          </>
        )}
      </div>
    </div>
  )
}
